import { Skeleton } from '@workspace/ui/components/skeleton';
import { cn } from '@workspace/ui/lib/utils';

// Loading placeholders that mirror the document agent layout. The splash screen imports these,
// so keep this file free of heavy imports.

const ComposerSkeleton = ({ isHero = false }: { isHero?: boolean }) => (
  <div
    className={cn(
      'flex flex-col justify-between bg-white p-3 border-border rounded-xl w-full',
      isHero ? 'h-32 border-2' : 'h-28 border',
    )}
  >
    <Skeleton className="bg-gray-60 rounded w-1/2 h-5" />
    <div className="flex justify-between items-center">
      <Skeleton className="bg-gray-60 rounded-full size-8" />
      <div className="flex items-center gap-1">
        <Skeleton className="bg-gray-60 rounded-lg w-28 h-8" />
        <Skeleton className="bg-gray-60 rounded-lg size-8" />
        {/* The hero's labelled submit shrinks to an icon button below sm. */}
        <Skeleton
          className={cn('bg-gray-60 rounded-lg size-8', isHero && 'sm:w-36 sm:h-10')}
        />
      </div>
    </div>
  </div>
);

export const AgentSidebarSkeleton = ({ isOpen }: { isOpen: boolean }) =>
  isOpen ? (
    <aside className="hidden lg:flex flex-col shrink-0 border-border border-r w-70">
      <div className="flex justify-between items-center px-3 py-2 border-b">
        <p className="font-semibold text-purple-500 text-base leading-6">Recent Chats</p>
        <Skeleton className="bg-gray-60 rounded-lg size-9" />
      </div>
      <div className="px-2 pt-2.5">
        <Skeleton className="bg-gray-60 rounded-lg w-full h-9" />
      </div>
      <p className="flex items-center px-2.5 h-9 font-medium text-gray-550 text-sm">Recents</p>
      <div className="space-y-2 px-2 pt-1">
        {[1, 2, 3, 4, 5].map((item) => (
          <div key={item} className="flex flex-col gap-1.5 px-2.5 py-1.5">
            <Skeleton className="bg-gray-60 rounded w-4/5 h-4" />
            <Skeleton className="bg-gray-60 rounded w-1/3 h-3" />
          </div>
        ))}
      </div>
    </aside>
  ) : (
    <aside className="hidden lg:block shrink-0 border-border border-x w-12.5">
      <Skeleton className="bg-gray-60 mx-auto mt-3 rounded-lg size-8" />
    </aside>
  );

// New chat: heading, the hero composer and the "start another way" cards.
export const AgentHeroSkeleton = ({ showStartOptions }: { showStartOptions: boolean }) => (
  <div
    role="status"
    aria-label="Loading"
    // center-safe: on a short phone screen the content starts at the top, as the page does.
    className="flex flex-col flex-1 justify-center-safe items-center gap-8 mx-auto py-6 w-full max-w-228"
  >
    {/* The heading and subheading wrap to two lines on phones. */}
    <div className="flex flex-col items-center gap-3 w-full">
      <Skeleton className="bg-gray-60 rounded-md w-11/12 sm:w-3/4 h-9 sm:h-12" />
      <Skeleton className="sm:hidden bg-gray-60 rounded-md w-1/2 h-9" />
      <Skeleton className="bg-gray-60 rounded-md w-full sm:w-2/3 h-5 sm:h-6" />
      <Skeleton className="sm:hidden bg-gray-60 rounded-md w-3/4 h-5" />
    </div>
    <ComposerSkeleton isHero />
    {showStartOptions ? (
      <div className="flex flex-col items-center gap-8 w-full">
        <Skeleton className="bg-gray-60 rounded w-48 h-5" />
        <div className="gap-5 grid grid-cols-1 sm:grid-cols-3 w-full">
          {[1, 2, 3].map((item) => (
            <div key={item} className="flex flex-col gap-4 p-5 border border-border rounded-xl">
              <div className="flex items-center gap-3">
                <Skeleton className="bg-gray-60 rounded size-5" />
                <Skeleton className="bg-gray-60 rounded w-2/3 h-5" />
              </div>
              <div className="space-y-2">
                <Skeleton className="bg-gray-60 rounded w-full h-3.5" />
                <Skeleton className="bg-gray-60 rounded w-4/5 h-3.5" />
              </div>
            </div>
          ))}
        </div>
      </div>
    ) : null}
  </div>
);

const AssistantLinesSkeleton = ({ widths }: { widths: string[] }) => (
  <div className="space-y-2.5">
    {widths.map((width) => (
      <Skeleton key={width} className={cn('bg-gray-60 rounded h-4', width)} />
    ))}
  </div>
);

const UserBubbleSkeleton = ({ width }: { width: string }) => (
  <div
    className={cn(
      'ml-auto bg-purple-50 px-4 py-3 border border-border rounded-[14px] rounded-br-none max-w-xl',
      width,
    )}
  >
    <Skeleton className="bg-purple-25 rounded w-full h-4" />
  </div>
);

// Ongoing chat: a few message turns and the chat composer, so a restored chat reads as a
// conversation from the first frame instead of the new-chat screen.
export const AgentChatSkeleton = () => (
  <div role="status" aria-label="Loading your chat" className="flex flex-col flex-1 min-h-0">
    <div className="flex flex-col flex-1 gap-8 mx-auto pt-6 w-full max-w-4xl overflow-hidden">
      <UserBubbleSkeleton width="w-2/3" />
      <AssistantLinesSkeleton widths={['w-full', 'w-11/12', 'w-3/5']} />
      <UserBubbleSkeleton width="w-2/5" />
      <AssistantLinesSkeleton widths={['w-10/12', 'w-full', 'w-2/3', 'w-1/2']} />
    </div>
    <div className="mx-auto mt-4 w-full max-w-228">
      <ComposerSkeleton />
    </div>
  </div>
);
